import { AppToastOutlet, useAppToastRef } from './app-toast-provider';
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { GetUserStatsApiResponseDto, ListCourseDto, ModuleDto, UserCourseDto, UserDto, UserModuleDto } from "../general/dtos";
import { Api } from "../general/api";
import { useApi } from "./api-provider";
import { Toast } from "primereact/toast";
import { useBookmarkValues } from '../general/bookmark-selection';
import { useUnsavedActions, useUnsavedDraft } from '../general/unsaved-changes-context';

export interface UserValidationErrors {
    first_name?: string;
    last_name?: string;
}

export interface UserManagementDataContextValue {
  toastRef: React.RefObject<Toast | null>;
  
  getUser: (sub: string) => Promise<void>;
  userLoading: boolean;
  userError: string | null;
  mode: "view" | "edit";
  selectedUserKey: string | null;
  selectedUser: UserDto | null;
  updateUserField: (field: keyof UserDto, value: any) => void;
  startEditUser: (sub: string) => void;
  validationErrors: UserValidationErrors;
  validateUser: () => string[] | null;
  saveUser: () => Promise<void>;
  cancelEditing: () => void;

  allModules: ModuleDto[];
  allModulesLoading: boolean;
  allModulesError: string | null;
  loadAllModules: () => Promise<void>;

  allCourses: ListCourseDto[];
  allCoursesLoading: boolean;
  allCoursesError: string | null;
  loadAllCourses: () => Promise<void>;

  userModules: Record<string, UserModuleDto[]>;
  userModulesLoading: boolean;
  userModulesError: string | null;
  loadUserModules: (userId: string) => Promise<void>;
  setUserModulesForUser: (userId: string, modules: UserModuleDto[]) => void;

  userCourses: Record<string, UserCourseDto[]>;
  userCoursesLoading: boolean;
  userCoursesError: string | null;
  loadUserCourses: (userId: string) => Promise<void>;
  setUserCoursesForUser: (userId: string, courses: UserCourseDto[]) => void;

  userStats: Record<string, GetUserStatsApiResponseDto>;
  userStatsLoading: boolean;
  userStatsError: string | null;
  loadUserStats: (userId: string) => Promise<void>;
}

export const UserManagementDataContext = createContext<UserManagementDataContextValue | null>(null);

export function UserManagementDataProvider({ children }: { children: React.ReactNode }) {
  const toastRef = useAppToastRef();
  const { requestAction, withoutWarning } = useUnsavedActions();
  const api: Api = useApi();
  // mode
  const [mode, setMode] = useState<"view" | "edit">("view");

  // user
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [userError, setUserError] = useState<string | null>(null);
  const [selectedUserKey, setSelectedUserKey] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [bookmarkedUserKeys] = useBookmarkValues('user');
  const userLoadGeneration = useRef(0);
  useEffect(() => {
    if (mode === 'edit' && (bookmarkedUserKeys.length !== 1 || bookmarkedUserKeys[0] !== selectedUserKey)) {
      userLoadGeneration.current++;
      setMode('view');
      setSelectedUserKey(null);
      setSelectedUser(null);
      setUserLoading(false);
      setUserError(null);
    }
  }, [bookmarkedUserKeys, selectedUserKey, mode]);
  const [validationErrors, setValidationErrors] = useState<UserValidationErrors>({});
  // modules
  const [allModules, setAllModules] = useState<ModuleDto[]>([]);
  const [allModulesLoading, setAllModulesLoading] = useState<boolean>(false);
  const [allModulesError, setAllModulesError] = useState<string | null>(null);
  const modulesInFlight = useRef<Promise<void> | null>(null);
  // courses
  const [allCourses, setAllCourses] = useState<ListCourseDto[]>([]);
  const [allCoursesLoading, setAllCoursesLoading] = useState<boolean>(false);
  const [allCoursesError, setAllCoursesError] = useState<string | null>(null);
  const coursesInFlight = useRef<Promise<void> | null>(null);
  // user modules
  const [userModules, setUserModules] = useState<Record<string, UserModuleDto[]>>({});
  const [userModulesLoading, setUserModulesLoading] = useState<boolean>(false);
  const [userModulesError, setUserModulesError] = useState<string | null>(null);
  const userModulesInFlight = useRef<Record<string, Promise<void> | null>>({});
  // user courses
  const [userCourses, setUserCourses] = useState<Record<string, UserCourseDto[]>>({});
  const [userCoursesLoading, setUserCoursesLoading] = useState<boolean>(false);
  const [userCoursesError, setUserCoursesError] = useState<string | null>(null);
  const userCoursesInFlight = useRef<Record<string, Promise<void> | null>>({});
  // user stats
  const [userStats, setUserStats] = useState<Record<string, GetUserStatsApiResponseDto>>({});
  const [userStatsLoading, setUserStatsLoading] = useState<boolean>(false);
  const [userStatsError, setUserStatsError] = useState<string | null>(null);
  const userStatsInFlight = useRef<Record<string, Promise<void> | null>>({});

  const getUser = useCallback(async (sub: string): Promise<void> => {
    const generation = ++userLoadGeneration.current;
    try {
        setUserLoading(true);
        setUserError(null);
        const result: UserDto = await api.getUser(sub);
        if (generation !== userLoadGeneration.current) return;
        guard.markSaved(result);
        setSelectedUser(result);
    } catch (error: any) {
        if (generation !== userLoadGeneration.current) return;
        const message: string = error.response?.data?.message ?? 'Failed to load user data';
        setUserError(message);
        setUserLoading(false);
        setSelectedUser(null);
    } finally {
        if (generation === userLoadGeneration.current) setUserLoading(false);
    }
  }, [api]);

  const validateUser = (): string[] | null => {
      setValidationErrors({});
      if (!selectedUser) return ["No user loaded"];
  
      const errors: string[] = [];
      const namedErrors: UserValidationErrors = {};
      const c = selectedUser;
  
      // first name required
      if (!c.first_name || c.first_name.trim().length === 0) {
          errors.push("User first name is required.");
          namedErrors.first_name = "First name is required.";
      }

      // last name required
      if (!c.last_name || c.last_name.trim().length === 0) {
          errors.push("User last name is required.");
          namedErrors.first_name = "Last name is required.";
      }

      setValidationErrors(namedErrors);
      return errors.length > 0 ? errors : null;
  }

  const saveUser = async(): Promise<void> => {
    if (selectedUser) {
        const errors: string[] | null = validateUser();
        toastRef?.current?.clear();
        if (errors) {
            toastRef?.current?.show({
                severity: "error",
                summary: "Validation failed",
                detail: (
                    <ol style={{ marginLeft: "1rem", paddingLeft: "1rem", marginTop: "0.25rem" }}>
                        {errors.map((e, i) => (
                            <li key={i}>{e}</li>
                        ))}
                    </ol>
                ),
                life: 20000
            });
            return;
        }
        
        const generation = userLoadGeneration.current;
        try {
            setUserLoading(true);
            const result: UserDto = await api.updateUser(selectedUser);
            if (generation !== userLoadGeneration.current) return;
            guard.markSaved(result);
            setSelectedUser(result);
            setMode("view");
            toastRef.current?.show({ severity: 'success', summary: 'User saved', life: 4000 });
        } catch (error: any) {
            if (generation !== userLoadGeneration.current) return;
            const message: string = error.response?.data?.message ?? 'Failed to save user data';
            setUserError(message);
            setUserLoading(false);
        } finally {
            if (generation === userLoadGeneration.current) setUserLoading(false);
        }
    }
  }

  const cancelEditing = useCallback(() => {
      userLoadGeneration.current++;
      setUserLoading(false);
      setMode("view");
  }, []);

  const startEditUser = useCallback((sub: string) => {
      setMode("edit");
      setSelectedUserKey(sub);
      getUser(sub);
    }, [getUser]);

  const updateUserField = (field: keyof UserDto, value: any) => {
    setSelectedUser(prev => {
        return prev ? { ...prev, [field]: value } : prev;
    });
  };
  
  const loadAllModules = async (): Promise<void> => {
    if (modulesInFlight.current) {
        return modulesInFlight.current;
    }

    setAllModulesLoading(true);
    setAllModulesError(null);

    const promise = api.getModules(0, 10000, '', '', '', '')
    .then((data) => {
      setAllModules(data.data);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllModulesError(message);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    });

    modulesInFlight.current = promise;
    return promise;
  };

  const loadAllCourses = async (): Promise<void> => {
    if (coursesInFlight.current) {
        return coursesInFlight.current;
    }

    setAllCoursesLoading(true);
    setAllCoursesError(null);

    const promise = api.searchCourses(0, 10000, '', '', '', '')
    .then((data) => {
      setAllCourses(data.data);
      setAllCoursesLoading(false);
      coursesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllCoursesError(message);
      setAllCoursesLoading(false);
      coursesInFlight.current = null;
    });

    coursesInFlight.current = promise;
    return promise;
  };

  const loadUserModules = async (userId: string): Promise<void> => {
    if (userModulesInFlight.current[userId]) {
        return userModulesInFlight.current[userId]!;
    }

    setUserModulesLoading(true);
    setUserModulesError(null);

    const promise = api.getUserModules(userId)
      .then((data) => {
        setUserModules(prev => ({ ...prev, [userId]: data.data }));
        setUserModulesLoading(false);
        userModulesInFlight.current[userId] = null;
      })
      .catch((error) => {
        const message: string = error.response?.data?.message ?? 'Load error';
        setUserModulesError(message);
        setUserModulesLoading(false);
        userModulesInFlight.current[userId] = null;
      });

    userModulesInFlight.current[userId] = promise;
    return promise;
  };

  const loadUserCourses = async (userId: string): Promise<void> => {
    if (userCoursesInFlight.current[userId]) {
        return userCoursesInFlight.current[userId]!;
    }

    setUserCoursesLoading(true);
    setUserCoursesError(null);

    const promise = api.getUserCourses(userId)
      .then((data) => {
        setUserCourses(prev => ({ ...prev, [userId]: data.data }));
        setUserCoursesLoading(false);
        userCoursesInFlight.current[userId] = null;
      })
      .catch((error) => {
        const message: string = error.response?.data?.message ?? 'Load error';
        setUserCoursesError(message);
        setUserCoursesLoading(false);
        userCoursesInFlight.current[userId] = null;
      });

    userCoursesInFlight.current[userId] = promise;
    return promise;
  };

   const loadUserStats = async (userId: string): Promise<void> => {
    if (userStatsInFlight.current[userId]) {
        return userStatsInFlight.current[userId]!;
    }

    // Mark loading
    setUserStatsLoading(true);
    setUserStatsError(null);

    const promise = api.getUserStats(userId)
      .then((data) => {
        setUserStats(prev => ({ ...prev, [userId]: data! }));
        setUserStatsLoading(false);
        userStatsInFlight.current[userId] = null;
      })
      .catch((error) => {
        const message: string = error.response?.data?.message ?? 'Load error';
        setUserStatsError(message);
        setUserStatsLoading(false);
        userStatsInFlight.current[userId] = null;
      });

    userStatsInFlight.current[userId] = promise;
    return promise;
  };

  useEffect(() => {
    loadAllModules();
    loadAllCourses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUserModulesForUser = (userId: string, modules: UserModuleDto[]): void => {
    setUserModules(prev => ({
      ...prev,
      [userId]: modules
    }));
  };

  const setUserCoursesForUser = (userId: string, courses: UserCourseDto[]): void => {
    setUserCourses(prev => ({
      ...prev,
      [userId]: courses
    }));
  };

  const guard = useUnsavedDraft({ active: mode === 'edit' && selectedUser !== null,
    value: selectedUser, parameter: 'user', save: saveUser, discard: cancelEditing });

  return (
    <UserManagementDataContext.Provider
      value={{
        toastRef,
        
        getUser,
        userLoading,
        userError,
        mode,
        selectedUserKey,
        selectedUser,
        updateUserField,
        startEditUser: sub => requestAction(() => startEditUser(sub)),
        validationErrors,
        validateUser,
        saveUser,
        cancelEditing: () => withoutWarning(cancelEditing),

        allModules,
        allModulesLoading,
        allModulesError,
        loadAllModules,

        allCourses,
        allCoursesLoading,
        allCoursesError,
        loadAllCourses,

        userModules,
        userModulesLoading,
        userModulesError,
        loadUserModules,
        setUserModulesForUser,

        userCourses,
        userCoursesLoading,
        userCoursesError,
        loadUserCourses,
        setUserCoursesForUser,

        userStats,
        userStatsLoading,
        userStatsError,
        loadUserStats,
      }}
    >
      {children}
      <AppToastOutlet toastRef={toastRef} />
    </UserManagementDataContext.Provider>
  );
}
